/**
 * separateOptions — splits line items into base lines and option lines.
 *
 * Extracted from QuoteReviewSend.js (2026-08-01, commit 1b — break circular
 * dependency between quote-builder/buildViewQuote.js and QuoteReviewSend).
 *
 * Options live in `line_items[]` with `areaLabel === 'Options'` (created via
 * the standard AddItemCascade flow with areaId:'options'). Match on multiple
 * aliases so `is_option` / `type: 'option'` markers also route correctly.
 *
 * Consumers:
 *   - buildViewQuote.js (the projection)
 *   - QuoteReviewSend.js (re-imports from here)
 *
 * @param {Array} lineItems — raw line items from the quote
 * @param {Array} [quoteOptions] — legacy `quote_options[]` array (dollar totals)
 * @returns {{ baseLines: Array, optionLines: Array }}
 */

// R20-15 §4a: branch on the stable areaId, not the display label. A rename of
// the seeded 'Options' area silently broke option-separation because the check
// was label-string equality — the only hard label dependency in the app.
// Keep the label alias as a fallback for older records that lack areaId.
const OPTION_AREA_IDS = new Set(['options', 'optional', 'optional_upgrades']);
const OPTION_LABELS = new Set(['options', 'optional', 'optional upgrades']);

export function isOptionLine(li) {
  const areaId = String(li?.areaId || li?.area_id || '').toLowerCase();
  if (areaId && OPTION_AREA_IDS.has(areaId)) return true;
  const label = String(li?.areaLabel || li?.area_label || '').toLowerCase();
  return OPTION_LABELS.has(label) || li?.is_option === true || li?.type === 'option' || li?.kind === 'option';
}

export function separateOptions(lineItems, quoteOptions) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const baseLines = items.filter((li) => !isOptionLine(li));
  const fromLineItems = items.filter(isOptionLine);
  const fromQuoteOptions = (Array.isArray(quoteOptions) ? quoteOptions : []).map((o, idx) => ({
    id: o.id || `qo-${idx}`,
    areaLabel: 'Options',
    lineDescription: o.description || '',
    productName: o.name || 'Option',
    total: typeof o.total === 'number' ? Math.round(o.total * 100) : 0,
    quantity: 1,
    unit: '',
  }));
  return { baseLines, optionLines: [...fromLineItems, ...fromQuoteOptions] };
}
